// The bare i18next singleton, the one src/i18n.ts configures: importing that
// module here would drag the whole i18n bootstrap into every caller.
import i18n from 'i18next';
import type { SupportedLanguage } from '../i18n';
import { tokenStorage } from '../utils/api-client';
import { profileService } from '../services/profile';

/** The pickable languages, in the order the switchers show them. */
export const LANGUAGE_OPTIONS: { value: SupportedLanguage; flag: string; label: string }[] = [
  { value: 'en', flag: '🇬🇧', label: 'English' },
  { value: 'it', flag: '🇮🇹', label: 'Italiano' },
];

/**
 * The language the UI is rendered in, narrowed to one the app actually ships.
 * i18next can hold a regional tag ('it-CH'), the components compare with 'it'.
 *
 * Components pass the tag they got from `useTranslation()` so that switching
 * language re-renders them; without an argument the singleton is read.
 */
export function currentLanguage(tag: string = i18n.language): SupportedLanguage {
  return tag?.startsWith('it') ? 'it' : 'en';
}

/**
 * Switch the interface language. For a signed-in user the choice is also stored
 * on the account, so emails and push notifications arrive in the language they
 * picked instead of the one guessed at sign-up.
 */
export async function changeAppLanguage(language: SupportedLanguage): Promise<void> {
  if (language === currentLanguage()) return;

  await i18n.changeLanguage(language);

  if (!tokenStorage.getToken()) return;

  try {
    await profileService.updateLanguage(language);
  } catch (error) {
    // The interface already switched; the account preference can wait.
    console.warn('Failed to persist the language preference:', error);
  }
}
